import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import vueRouter from '@/router/index';                                                         // ルート情報取得
import { OrganizationType } from '@/shared/type';                                               // 組織情報の型をimport
import HttpStatus from 'http-status-codes';                                                     // HTTPステータスコード取得用
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import SystemConst from '@/shared/system-const';

// reactiveDataの型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    isValid: boolean;                                                   // 入力チェック状況
    organizationId: string;                                             // 組織ID
    displayOrganizationId: string;                                      // 表示用組織ID
    organization: OrganizationType;                                     // 組織情報
};

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.HEIGHT;              // フォーム高さ調整用変数
        const router = vueRouter;                                       // ルータ
        const commonRef = ref<any>(null);                               // 共通ダイアログ
        const initialPulldown = { text: '', value: '' };

        const state = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            isValid: true,
            organizationId: '',
            displayOrganizationId: '',
            organization: {
                affiliationCode: '',                                    // 所属コード
                displayOrder: '',                                       // 表示順
                location: initialPulldown,                              // 地域
                locationName: '',                                       // 地域名称
                organizationType: initialPulldown,                      // 組織型
                organizationTypeName: '',                               // 組織型名称
                organizationName: '',                                   // 組織名称
                organizationNameKana: '',                               // 組織名称（かな）
                domain: '',                                             // 組織ドメイン
                representMail: '',                                      // 代表メールアドレス
                canSalaryGroup: false,                                  // 給与グループ作成
                canPublicRelationsGroup: false,                         // 広報グループ作成
                canOfficialDocumentGroup: false,                        // 公文書グループ作成
                canCms: false,                                          // CMS利用可否
                remarks: '',                                            // 備考
            },
        });

        /**
         * 初期表示
         */
        created();
        async function created() {

            // $route.paramsの組織IDをstateの、back側に渡すためのorganizationIdと
            // 表示用のdisplayOrganizationIdに設定する
            state.organizationId = ctx.root.$route.params.id;
            state.displayOrganizationId = ctx.root.$route.params.id;
    
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-bank-remove');

            // タイトルを「組織削除」に設定
            ctx.root.$store.commit('setMainTitle', '組織削除');

            // getOrganization()を呼び組織情報を取得する
            getOrganization();

            // 取得した組織情報を画面に表示する

            return true;
        }

        /**
         * 組織情報取得
         */
        async function getOrganization() {

            // 組織情報取得APIを呼び出す
            // パラメータ:組織ID
            const response = await ctx.root.$store.dispatch('organization/getOrganization',
                {
                    organizationId: state.organizationId
                });

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 404の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 「対象の組織情報は存在しません」という旨のダイアログを表示し、組織一覧画面に遷移する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/organizations/list');
                }
                // 404以外の場合
                // エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // HTTPステータスコードが200の場合
            // 取得したデータを入力フォームに表示する
            state.organization = response.data.organization;
        }

        /**
         * 削除ボタン押下時に「組織ID」存在チェック
         * 存在チェックが全て通った後、組織情報を削除する
         */
        async function del() {

            // 削除処理実行確認ダイアログを表示する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);

            commonRef.value.displayConfirmationMessageDialog(displayMessage);

            // 「はい」をクリックした場合
            // processingData()メソッドを呼び組織情報存在チェックと組織情報削除を行う

            // 「いいえ」をクリックした場合、ダイアログを閉じる
        }

        /**
         * 確認ダイアログの「はい」を押下時の処理
         * 組織情報存在チェックと組織情報削除を行う
         */
        async function processingData() {

            // 組織情報削除APIを呼び出す
            // パラメータ:組織ID
            const response = await ctx.root.$store.dispatch('organization/deleteOrganization',
                {
                    organizationId: state.organizationId
                });

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 404の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 「対象の組織情報は存在しません」という旨のダイアログを表示し、組織一覧画面に遷移する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/organizations/list');
                }

                // 400の場合、back側から返却されたメッセージ「対象の組織情報は削除できない」旨のダイアログを表示する
                // (errorDisplayTransitionControlメソッド内でメッセージの表示を行うため、ここでは処理なし)
                // 400、404以外の場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // HTTPステータスコードが200の場合
            // 削除完了ダイアログを表示し、組織一覧画面に遷移する
            commonRef.value.displayMessageTransitionDialog(response, '/organizations/list');

            return;
        }

        /**
         * 組織削除画面にて、キャンセルボタン押下時の処理
         */
        function back() {

            // 組織削除画面を閉じ、組織一覧画面へ戻る
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            commonRef,
            formHeight,
            del,
            processingData,
            onResize,
            back,
        };
    }
});
